import { ButtonBase, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { COLORS } from '@/styles/tokens/colors';

export interface ChatSuggestionProps {
  /** The suggested question to display. */
  question: string;
  /** Invoked when the suggestion is clicked. No-op while `loading` is true. */
  onClick: () => void;
  /** When true, the suggestion is dimmed and clicks are ignored. */
  loading?: boolean;
  /** Optional leading icon rendered before the question text. */
  icon?: ReactNode;
}

/**
 * Presentational suggestion chip shared across chat surfaces. Owns the chip
 * layout, hover, and loading affordances; callers wire up the click behavior.
 */
export function ChatSuggestion({
  question,
  onClick,
  loading = false,
  icon,
}: ChatSuggestionProps) {
  return (
    <ButtonBase
      disabled={loading}
      onClick={onClick}
      sx={{
        alignItems: 'center',
        display: 'flex',
        py: 1.5,
        px: 2,
        gap: 1.5,
        // Hug the question text and left-align within the column so the chips
        // read as a scannable list rather than centered banners.
        alignSelf: 'flex-start',
        borderRadius: '16px',
        border: `1px solid ${COLORS.NAVY[100]}`,
        backgroundColor: COLORS.NAVY[50],
        opacity: loading ? 0.6 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: COLORS.NAVY[500],
        },
      }}
    >
      {icon}
      <Typography
        variant="body-xs"
        sx={{
          fontWeight: 500,
          color: COLORS.TEXT.PRIMARY,
        }}
      >
        {question}
      </Typography>
    </ButtonBase>
  );
}
